import { useState, useEffect, Fragment } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Checkbox,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import AppDialog from "../../General/Dialog/AppDialog";
import ConfirmDialog from "../../General/Dialog/ConfirmDialog";
import { useToast } from "../../../context/ToastContext";
import {
  getIndividualItemTypes,
  getIndividualDependencyItemTypes,
  syncIndividualDependencyItemTypes,
} from "../../../services/IndividualLiquidationService";
import { useTheme } from "@mui/material/styles";

const CATEGORY_COLORS = { capital: "#1976d2", honorarios: "#2e7d32", gastos: "#e65100" };
const CATEGORY_ORDER = ["capital", "honorarios", "gastos"];
const RATE_OPTIONS = [
  { value: "", label: "—" },
  { value: "pasiva", label: "Tasa Pasiva" },
  { value: "banco_cordoba", label: "Bco. Córdoba" },
  { value: "tim", label: "TIM" },
];
// El interés (no los punitorios) admite además "interés simple".
const INTEREST_RATE_OPTIONS = [...RATE_OPTIONS, { value: "simple", label: "Interés simple" }];

export default function IndividualDependencyItemTypesDialog({ open, onClose, dependency, onSaved }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { showToast } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    if (!open || !dependency) return;
    const load = async () => {
      setLoading(true);
      try {
        const [typesRes, cfgRes] = await Promise.all([
          getIndividualItemTypes({ perPage: 200, page: 1, search: "" }),
          getIndividualDependencyItemTypes(dependency.id),
        ]);
        const types = typesRes.data?.data ?? [];
        const configured = cfgRes.data?.item_types ?? [];
        const configMap = {};
        configured.forEach((t) => {
          configMap[t.id] = t.pivot ?? {};
        });
        const mapped = types.map((t) => ({
          id: t.id,
          name: t.name,
          category: t.category,
          default_amount_mode: t.default_amount_mode,
          enabled: configMap[t.id]?.is_enabled ?? false,
          jus_multiplier: configMap[t.id]?.jus_multiplier ?? "",
          default_interest_rate_type: configMap[t.id]?.default_interest_rate_type ?? "",
          default_interest_plus_rate: configMap[t.id]?.default_interest_plus_rate ?? "",
          default_interest_plus_period: configMap[t.id]?.default_interest_plus_period ?? "mensual",
          default_punitorio_rate_type: configMap[t.id]?.default_punitorio_rate_type ?? "",
          default_punitorio_plus_rate: configMap[t.id]?.default_punitorio_plus_rate ?? "",
          default_punitorio_plus_period: configMap[t.id]?.default_punitorio_plus_period ?? "mensual",
          sort_order: configMap[t.id]?.sort_order ?? t.sort_order ?? 0,
        }));
        // Agrupados por categoría (capital → honorarios → gastos) y luego por orden.
        mapped.sort(
          (a, b) =>
            CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category) ||
            (a.sort_order ?? 0) - (b.sort_order ?? 0),
        );
        setRows(mapped);
      } catch {
        showToast("No se pudo cargar la configuración.", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, dependency, showToast]);

  const update = (idx, field, value) =>
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  const buildPayload = () =>
    rows
      .filter((r) => r.enabled)
      .map((r) => ({
        id: r.id,
        jus_multiplier: r.jus_multiplier !== "" ? parseFloat(r.jus_multiplier) : null,
        is_enabled: true,
        sort_order: parseInt(r.sort_order, 10) || 0,
        default_interest_rate_type: r.default_interest_rate_type || null,
        default_interest_plus_rate: r.default_interest_plus_rate !== "" ? parseFloat(r.default_interest_plus_rate) : null,
        default_interest_plus_period: r.default_interest_plus_rate !== "" ? r.default_interest_plus_period || "mensual" : null,
        default_punitorio_rate_type: r.default_punitorio_rate_type || null,
        default_punitorio_plus_rate: r.default_punitorio_plus_rate !== "" ? parseFloat(r.default_punitorio_plus_rate) : null,
        default_punitorio_plus_period: r.default_punitorio_plus_rate !== "" ? r.default_punitorio_plus_period || "mensual" : null,
      }));

  const persist = async (item_types, okMsg) => {
    setSaveLoading(true);
    try {
      await syncIndividualDependencyItemTypes(dependency.id, { item_types });
      showToast(okMsg, "success");
      onSaved?.();
      onClose();
    } catch (e) {
      showToast(e?.response?.data?.message ?? "Error al guardar la configuración.", "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSave = () => persist(buildPayload(), "Configuración guardada.");

  const handleReset = () => {
    setResetOpen(false);
    setRows((prev) => prev.map((r) => ({ ...r, enabled: false })));
    persist([], "Juzgado restablecido: usa los rubros por defecto.");
  };

  const enabledCount = rows.filter((r) => r.enabled).length;

  return (
    <>
      <AppDialog
        open={open}
        onClose={onClose}
        title={`Configurar rubros — ${dependency?.name ?? ""}`}
        subtitle="Activá los rubros, el multiplicador JUS y la tasa por defecto para este juzgado"
        maxWidth="md"
        fullWidth
        stickyHeader
        actionsSx={{ flexWrap: "wrap", rowGap: 1 }}
        actions={
          <>
            <Button
              onClick={() => setResetOpen(true)}
              disabled={saveLoading || loading}
              color="error"
              startIcon={<RestartAltRoundedIcon />}
              sx={{ mr: "auto", borderRadius: 2 }}
            >
              Restablecer
            </Button>
            <Button onClick={onClose} disabled={saveLoading}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
              onClick={handleSave}
              disabled={saveLoading || loading}
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Guardar
            </Button>
          </>
        }
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: 12, opacity: 0.6 }}>
              {enabledCount === 0
                ? "Sin rubros activos: este juzgado usará los rubros por defecto del catálogo."
                : `${enabledCount} rubro${enabledCount !== 1 ? "s" : ""} activo${enabledCount !== 1 ? "s" : ""}.`}
            </Typography>
            <TableContainer sx={{ borderRadius: 2, border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.09)" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }}>
                    <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Activo</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Rubro</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Categoría</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Mult. JUS</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Tasa por defecto</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>+ % extra</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Período</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Punit. tasa</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Punit. + %</TableCell>
                    <TableCell sx={{ fontWeight: 900, fontSize: 11 }}>Orden</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => {
                    const firstOfCategory = idx === 0 || rows[idx - 1].category !== row.category;
                    return (
                      <Fragment key={row.id}>
                        {firstOfCategory && (
                          <TableRow>
                            <TableCell
                              colSpan={10}
                              sx={{
                                py: 0.6,
                                bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.025)",
                                borderBottom: "none",
                              }}
                            >
                              <Typography sx={{ fontWeight: 900, fontSize: 10, letterSpacing: 0.6, textTransform: "uppercase", color: CATEGORY_COLORS[row.category] ?? "text.secondary" }}>
                                {row.category}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={{ opacity: row.enabled ? 1 : 0.45 }}>
                          <TableCell>
                            <Checkbox size="small" checked={row.enabled} onChange={(e) => update(idx, "enabled", e.target.checked)} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{row.name}</TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontWeight: 900, color: CATEGORY_COLORS[row.category] ?? "text.secondary", textTransform: "uppercase", fontSize: 10 }}>
                              {row.category}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={row.jus_multiplier}
                              onChange={(e) => update(idx, "jus_multiplier", e.target.value)}
                              disabled={!row.enabled}
                              type="number"
                              sx={{ width: 100 }}
                              InputProps={{ startAdornment: <InputAdornment position="start">×</InputAdornment> }}
                              placeholder="—"
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <Select value={row.default_interest_rate_type} onChange={(e) => update(idx, "default_interest_rate_type", e.target.value)} disabled={!row.enabled} displayEmpty>
                                {INTEREST_RATE_OPTIONS.map((o) => (
                                  <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={row.default_interest_plus_rate}
                              onChange={(e) => update(idx, "default_interest_plus_rate", e.target.value)}
                              disabled={!row.enabled}
                              type="number"
                              sx={{ width: 80 }}
                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                              placeholder="—"
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                value={row.default_interest_plus_period || "mensual"}
                                onChange={(e) => update(idx, "default_interest_plus_period", e.target.value)}
                                disabled={!row.enabled || !row.default_interest_plus_rate}
                              >
                                <MenuItem value="mensual">Mensual</MenuItem>
                                <MenuItem value="anual">Anual</MenuItem>
                                <MenuItem value="diario">Diario</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" sx={{ minWidth: 110 }}>
                              <Select
                                value={row.default_punitorio_rate_type}
                                onChange={(e) => update(idx, "default_punitorio_rate_type", e.target.value)}
                                disabled={!row.enabled}
                                displayEmpty
                              >
                                {RATE_OPTIONS.map((o) => (
                                  <MenuItem key={o.value} value={o.value}>
                                    {o.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={row.default_punitorio_plus_rate}
                              onChange={(e) => update(idx, "default_punitorio_plus_rate", e.target.value)}
                              disabled={!row.enabled || !row.default_punitorio_rate_type}
                              type="number"
                              sx={{ width: 80 }}
                              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                              placeholder="—"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" value={row.sort_order} onChange={(e) => update(idx, "sort_order", e.target.value)} disabled={!row.enabled} type="number" sx={{ width: 70 }} />
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </AppDialog>

      <ConfirmDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onConfirm={handleReset}
        loading={saveLoading}
        title="Restablecer juzgado"
        description={`Se quitarán todos los rubros configurados de «${dependency?.name ?? ""}». El juzgado pasará a usar los rubros por defecto del catálogo. ¿Continuar?`}
        confirmText="Restablecer"
        confirmColor="error"
      />
    </>
  );
}
